import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { buildForecastPayload } from 'src/features/forecastPaginated/utils/buildForecastPayload';
import {
  getForecastSessionsQueryKey,
  getSessionForecastDataQueryPrefix,
} from 'src/keys';
import { useSkipProductForecast } from 'src/services/api/skipProductForecast';
import { useUpdateProductForecast } from 'src/services/api/updateProductForecast';
import forecastService from 'src/services/forecast/forecastService';
import { useBoundStore } from 'src/store/store';

export const useForecastPaginatedUpdate = (
  sessionId: string,
  sku: string,
  isDestocking = false,
) => {
  const queryClient = useQueryClient();
  const { mutate: updateProductForecast } = useUpdateProductForecast();
  const { mutate: skipProductForecast } = useSkipProductForecast();
  const { t } = useTranslation('forecast');
  const getStoreData = useCallback(() => {
    return useBoundStore.getState().forecastData[
      forecastService.getStoreKey(sku, sessionId)
    ];
  }, [sessionId, sku]);
  const getSetForecastData = useCallback(() => {
    return useBoundStore.getState().setForecastData;
  }, []);

  const updateForecastProposal = useCallback(
    (callback: () => void) => {
      const storeData = getStoreData();
      const pushToApiData = buildForecastPayload(storeData, isDestocking);
      updateProductForecast(
        { sessionId, sku, forecasts: pushToApiData },
        {
          onSuccess: () => {
            const latestStoreData = getStoreData();
            const setForecastData = getSetForecastData();
            if (latestStoreData) {
              const updatedData = latestStoreData.map((item) => {
                if (typeof item.forecastProposal === 'number') {
                  return {
                    ...item,
                    validatedForecast: item.forecastProposal,
                  };
                }
                return item;
              });
              setForecastData(sku, updatedData, sessionId);
            }
            void queryClient.invalidateQueries({
              queryKey: getForecastSessionsQueryKey(),
            });
            void queryClient.invalidateQueries({
              queryKey: getSessionForecastDataQueryPrefix(sessionId),
            });
            callback();
            toast.success(t('Success'), {
              description: t('Forecast updated successfully'),
            });
          },
          onError: () => {
            toast.error(t('Error'), {
              description: t('Failed to update forecast'),
              action: {
                label: t('Retry'),
                onClick: () => {
                  updateForecastProposal(callback);
                },
              },
            });
          },
        },
      );
    },
    [
      getStoreData,
      getSetForecastData,
      sessionId,
      sku,
      updateProductForecast,
      t,
      isDestocking,
      queryClient,
    ],
  );

  const skipForecastProposal = useCallback(
    (callback: () => void) => {
      skipProductForecast(
        { sessionId, sku },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: getForecastSessionsQueryKey(),
            });
            void queryClient.invalidateQueries({
              queryKey: getSessionForecastDataQueryPrefix(sessionId),
            });
            callback();
            toast.success(t('Success'), {
              description: t('Forecast skipped successfully'),
            });
          },
          onError: () => {
            toast.error(t('Error'), {
              description: t('Failed to skip forecast'),
              action: {
                label: t('Retry'),
                onClick: () => {
                  skipForecastProposal(callback);
                },
              },
            });
          },
        },
      );
    },
    [queryClient, sessionId, sku, skipProductForecast, t],
  );

  return {
    updateForecastProposal,
    skipForecastProposal,
  };
};
